import type { UserInfo } from './optimizely.types';
import { ParsedUrlQuery } from 'querystring';
import { enums, setLogLevel } from '@optimizely/react-sdk';
import { getOptimizelyOverrides } from './optimizelyOverrides';
import { createOptimizelyReactSDKClient } from './hooks/useOptimizelyProviderProps';
import { getDataFile } from './optimizelyDataFile';

type OptimizelyServerRequest = {
    featureKey: string;
    cookies: Record<string, string | undefined>;
    query: ParsedUrlQuery;
    userId?: string;
    userAgent?: string;
    overrideAttributes?: UserInfo['attributes'];
};

export const getOptimizelyUserInfo = (
    anonId: string,
    userAgent: string | undefined
): UserInfo => {
    return {
        id: anonId,
        attributes: {
            $opt_user_agent: userAgent
        }
    };
};

/**
 * This method is for us to be able to get optimizely information on the server
 * we need to get the optimizely SDK and override the user id, since otherwise
 * it loads with an `undefined` user id and it doesn't activate the flag
 *
 * To get the required variables you can use the following in your `getServerSideProps`:
 *
```
const getServerSideProps: GetServerSideProps = async ({
    req,
    res,
    query
}) => {
    const allCookies = cookies.get({ req });
    const anonId = getAnonId({ req, res });

    const isFeatureEnabled = await getOptimizelyFeatureFlagOnServer({
        featureKey: 'your-feature-key',
        cookies: allCookies,
        query,
        userId: anonId,
        userAgent: req.headers['user-agent']
    });

    if (!isFeatureEnabled) {
        return {
            redirect: {
                destination: `/your-redirect/`,
                permanent: false
            }
        };
    }
}
```
*/
export const getOptimizelyFeatureFlagOnServer = async ({
    featureKey,
    cookies,
    query,
    userId,
    userAgent,
    overrideAttributes
}: OptimizelyServerRequest) => {
    const optimizelyOverrides = getOptimizelyOverrides(cookies, query);
    if (featureKey in optimizelyOverrides.features) {
        return optimizelyOverrides.features[featureKey];
    }

    const dataFile = await getDataFile();
    const optimizely = createOptimizelyReactSDKClient(dataFile || {});

    return (
        // eslint-disable-next-line no-restricted-properties
        optimizely?.isFeatureEnabled(featureKey, userId, {
            $opt_user_agent: userAgent,
            ...overrideAttributes
        }) ?? false
    );
};

// Logging docs:
// https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript
const isDev = process.env.NODE_ENV !== 'production';
if (isDev) {
    // Change here to increase logging levels in development
    setLogLevel(enums.LOG_LEVEL.WARNING);
}
